import React, { useEffect, useMemo, useRef, useState } from "react";
import { TerminalTheme } from "../types";
import { Command, Search } from "lucide-react";

// Linear-style command palette — Cmd+K / Ctrl+K opens, fuzzy-search, arrow
// keys + enter to select, Esc to close. Per the competitive research, this
// is table-stakes for an operator console steering 10+ agents at speed
// (keyboard-first triage is 3× faster than mouse).
//
// Self-contained: takes a flat command list as a prop. Parent (App.tsx)
// derives commands from current state (agents + tabs + actions) and passes
// them in. The palette doesn't know about any state directly.

export interface PaletteCommand {
  id: string;
  label: string;
  /** Short kind tag shown right-aligned for quick scan (e.g. "tab", "agent", "action"). */
  kind?: string;
  /** Optional keyboard hint (e.g. "1", "G then F"). Display-only. */
  shortcut?: string;
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  commands: PaletteCommand[];
  theme: TerminalTheme;
}

function fuzzyMatch(query: string, label: string): boolean {
  // Tiny case-insensitive subsequence match — every char of query must
  // appear in label in order. Good enough for ~50-command palettes.
  if (!query) return true;
  const q = query.toLowerCase();
  const l = label.toLowerCase();
  let qi = 0;
  for (let li = 0; li < l.length && qi < q.length; li++) {
    if (l[li] === q[qi]) qi++;
  }
  return qi === q.length;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, commands, theme }) => {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // Focus the input on the next tick so React renders the modal first.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(
    () => commands.filter((c) => fuzzyMatch(query, c.label)),
    [commands, query]
  );

  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIdx]);

  if (!open) return null;

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filtered[activeIdx];
      if (cmd) {
        onClose();
        cmd.action();
      }
    }
  };

  return (
    <div
      // Modal scrim
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        // Modal body — stop propagation so clicks inside don't dismiss
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKey}
        style={{
          backgroundColor: theme.inputBg,
          borderColor: theme.accent,
          color: theme.fg,
        }}
        className="w-[min(640px,92vw)] max-h-[60vh] border-2 rounded shadow-2xl font-mono overflow-hidden flex flex-col"
      >
        {/* Search input */}
        <div
          style={{ borderBottomColor: theme.border, backgroundColor: theme.bg }}
          className="flex items-center gap-2 border-b px-3 py-2"
        >
          <Search size={14} style={{ color: theme.accent }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Type to filter — agents · tabs · actions"
            style={{ color: theme.fg, caretColor: theme.accent }}
            className="flex-1 bg-transparent text-sm outline-none border-none"
            spellCheck={false}
            autoComplete="off"
          />
          <span
            style={{ color: theme.systemText, borderColor: theme.border, backgroundColor: theme.inputBg }}
            className="text-[10px] border rounded px-1.5 py-0.5 flex items-center gap-1"
          >
            <Command size={10} />K
          </span>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div style={{ color: theme.systemText }} className="text-sm text-center py-8">
              No commands match "{query}".
            </div>
          ) : (
            filtered.map((c, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={c.id}
                  type="button"
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => {
                    onClose();
                    c.action();
                  }}
                  style={{
                    backgroundColor: isActive ? `${theme.accent}1a` : "transparent",
                    color: isActive ? theme.accent : theme.fg,
                    borderLeftColor: isActive ? theme.accent : "transparent",
                  }}
                  className="w-full text-left px-3 py-2 border-l-2 flex items-center justify-between gap-3 cursor-pointer text-sm"
                >
                  <span className="flex-1 truncate">{c.label}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.kind && (
                      <span
                        style={{ color: theme.systemText, borderColor: theme.border, backgroundColor: theme.bg }}
                        className="text-[9px] border rounded px-1.5 py-0.5 uppercase"
                      >
                        {c.kind}
                      </span>
                    )}
                    {c.shortcut && (
                      <span style={{ color: theme.systemText }} className="text-[10px] font-mono">
                        {c.shortcut}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{ borderTopColor: theme.border, backgroundColor: theme.bg, color: theme.systemText }}
          className="border-t px-3 py-1.5 text-[10px] flex items-center justify-between"
        >
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};
